/**
 * ============================================================================
 * INDEXEDDB SERVICE v1.0.0 - OFFLINE STORAGE
 * ============================================================================
 *
 * Verwaltet IndexedDB für Offline-Datenspeicherung:
 * - Shopping Lists mit Items
 * - Calendar Appointments
 * - Sync Queue für Offline-Mutations
 *
 * @version 1.0.0
 * @date 2026-01-16
 */

const DB_NAME = 'planivio_offline';
const DB_VERSION = 1;

// Store-Konfiguration
const STORES = {
  shopping_lists: {
    keyPath: 'id',
    indexes: [
      { name: 'updated_at', keyPath: 'updated_at', unique: false },
      { name: 'status', keyPath: 'status', unique: false }
    ]
  },
  calendar_appointments: {
    keyPath: 'id',
    indexes: [
      { name: 'date', keyPath: 'date', unique: false },
      { name: 'updated_at', keyPath: 'updated_at', unique: false }
    ]
  },
  sync_queue: {
    keyPath: 'id',
    autoIncrement: true,
    indexes: [
      { name: 'type', keyPath: 'type', unique: false },
      { name: 'created_at', keyPath: 'created_at', unique: false },
      { name: 'status', keyPath: 'status', unique: false }
    ]
  },
  meta: {
    keyPath: 'key'
  }
};

class IndexedDBService {
  constructor() {
    this.db = null;
    this.dbPromise = null;
  }

  // ============================================================================
  // DATABASE MANAGEMENT
  // ============================================================================

  /**
   * Öffnet oder erstellt die IndexedDB Datenbank
   * @returns {Promise<IDBDatabase>}
   */
  async openDB() {
    if (this.db) {
      return this.db;
    }

    // Verhindere mehrfache parallele Öffnungsversuche
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      console.log('📦 Opening IndexedDB:', DB_NAME);

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ IndexedDB open error:', request.error);
        this.dbPromise = null;
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        console.log('📦 Upgrading IndexedDB schema...');
        const db = event.target.result;

        // Stores erstellen
        for (const [storeName, config] of Object.entries(STORES)) {
          if (!db.objectStoreNames.contains(storeName)) {
            const storeOptions = {
              keyPath: config.keyPath
            };
            if (config.autoIncrement) {
              storeOptions.autoIncrement = true;
            }

            const store = db.createObjectStore(storeName, storeOptions);

            // Indexes erstellen
            if (config.indexes) {
              for (const index of config.indexes) {
                store.createIndex(index.name, index.keyPath, { unique: index.unique });
              }
            }

            console.log(`✅ Created store: ${storeName}`);
          }
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * Schließt die Datenbankverbindung
   */
  closeDB() {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.dbPromise = null;
      console.log('📦 IndexedDB closed');
    }
  }

  // ============================================================================
  // GENERIC CRUD OPERATIONS
  // ============================================================================

  /**
   * Speichert ein Item in einem Store
   */
  async put(storeName, item) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      const request = store.put(item);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Holt ein Item anhand des Keys
   */
  async get(storeName, key) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);

      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Holt alle Items aus einem Store
   */
  async getAll(storeName) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);

      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Löscht ein Item anhand des Keys
   */
  async delete(storeName, key) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      const request = store.delete(key);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Leert einen kompletten Store
   */
  async clearStore(storeName) {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      const request = store.clear();

      request.onsuccess = () => {
        console.log(`🗑️ Store cleared: ${storeName}`);
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================================
  // SHOPPING LISTS
  // ============================================================================

  /**
   * Speichert alle Shopping-Listen (ersetzt bestehende)
   */
  async saveShoppingLists(lists) {
    try {
      const db = await this.openDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction('shopping_lists', 'readwrite');
        const store = transaction.objectStore('shopping_lists');

        // Erst alle löschen, dann neu einfügen
        const clearRequest = store.clear();

        clearRequest.onsuccess = () => {
          let saved = 0;

          for (const list of lists) {
            const putRequest = store.put({
              ...list,
              cached_at: new Date().toISOString()
            });

            putRequest.onsuccess = () => {
              saved++;
              if (saved === lists.length) {
                console.log(`📦 Saved ${saved} shopping lists to IndexedDB`);
                resolve(saved);
              }
            };
          }

          // Falls keine Listen vorhanden
          if (lists.length === 0) {
            resolve(0);
          }
        };

        clearRequest.onerror = () => reject(clearRequest.error);
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('❌ Error saving shopping lists:', error);
      throw error;
    }
  }

  /**
   * Holt alle Shopping-Listen aus dem Cache
   */
  async getShoppingLists() {
    try {
      const lists = await this.getAll('shopping_lists');
      console.log(`📦 Retrieved ${lists.length} shopping lists from IndexedDB`);
      return lists;
    } catch (error) {
      console.error('❌ Error getting shopping lists:', error);
      return [];
    }
  }

  /**
   * Speichert eine einzelne Shopping-Liste
   */
  async saveShoppingList(list) {
    try {
      await this.put('shopping_lists', {
        ...list,
        cached_at: new Date().toISOString()
      });
      console.log(`📦 Saved shopping list: ${list.name}`);
      return true;
    } catch (error) {
      console.error('❌ Error saving shopping list:', error);
      return false;
    }
  }

  /**
   * Aktualisiert ein Item in einer Shopping-Liste
   */
  async updateShoppingItem(listId, itemId, updates) {
    try {
      const list = await this.get('shopping_lists', listId);

      if (!list) {
        console.warn('⚠️ Shopping list not found in cache:', listId);
        return false;
      }

      // Items aktualisieren
      const updatedItems = (list.items || []).map(item => {
        if (item.id === itemId || String(item.id) === String(itemId)) {
          return { ...item, ...updates };
        }
        return item;
      });

      // Liste speichern
      await this.put('shopping_lists', {
        ...list,
        items: updatedItems,
        updated_at: new Date().toISOString(),
        cached_at: new Date().toISOString()
      });

      console.log(`📦 Updated item ${itemId} in list ${listId}`);
      return true;
    } catch (error) {
      console.error('❌ Error updating shopping item:', error);
      return false;
    }
  }

  // ============================================================================
  // CALENDAR APPOINTMENTS
  // ============================================================================

  /**
   * Speichert alle Termine (ersetzt bestehende)
   */
  async saveAppointments(appointments) {
    try {
      const db = await this.openDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction('calendar_appointments', 'readwrite');
        const store = transaction.objectStore('calendar_appointments');

        const clearRequest = store.clear();

        clearRequest.onsuccess = () => {
          let saved = 0;

          for (const appointment of appointments) {
            const putRequest = store.put({
              ...appointment,
              cached_at: new Date().toISOString()
            });

            putRequest.onsuccess = () => {
              saved++;
              if (saved === appointments.length) {
                console.log(`📦 Saved ${saved} appointments to IndexedDB`);
                resolve(saved);
              }
            };
          }

          if (appointments.length === 0) {
            resolve(0);
          }
        };

        clearRequest.onerror = () => reject(clearRequest.error);
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.error('❌ Error saving appointments:', error);
      throw error;
    }
  }

  /**
   * Holt alle Termine aus dem Cache
   */
  async getAppointments() {
    try {
      const appointments = await this.getAll('calendar_appointments');
      console.log(`📦 Retrieved ${appointments.length} appointments from IndexedDB`);
      return appointments;
    } catch (error) {
      console.error('❌ Error getting appointments:', error);
      return [];
    }
  }

  /**
   * Holt Termine für einen Datumsbereich
   */
  async getAppointmentsByDateRange(startDate, endDate) {
    try {
      const allAppointments = await this.getAppointments();

      const filtered = allAppointments.filter(apt => {
        const aptDate = apt.date || apt.start_date;
        return aptDate >= startDate && aptDate <= endDate;
      });

      return filtered;
    } catch (error) {
      console.error('❌ Error filtering appointments:', error);
      return [];
    }
  }

  // ============================================================================
  // SYNC QUEUE
  // ============================================================================

  /**
   * Fügt eine Operation zur Sync-Queue hinzu
   */
  async addToSyncQueue(operation) {
    try {
      const queueItem = {
        ...operation,
        created_at: new Date().toISOString(),
        status: 'pending',
        retry_count: 0
      };

      await this.put('sync_queue', queueItem);
      console.log(`📦 Added to sync queue: ${operation.type} ${operation.operation}`);

      return true;
    } catch (error) {
      console.error('❌ Error adding to sync queue:', error);
      return false;
    }
  }

  /**
   * Holt alle ausstehenden Sync-Operationen
   */
  async getSyncQueue() {
    try {
      const queue = await this.getAll('sync_queue');
      return queue.filter(item => item.status === 'pending');
    } catch (error) {
      console.error('❌ Error getting sync queue:', error);
      return [];
    }
  }

  /**
   * Gibt die Anzahl ausstehender Sync-Operationen zurück
   */
  async getSyncQueueCount() {
    const queue = await this.getSyncQueue();
    return queue.length;
  }

  /**
   * Markiert eine Sync-Operation als verarbeitet
   */
  async markSyncItemProcessed(id) {
    try {
      await this.delete('sync_queue', id);
      console.log(`📦 Removed from sync queue: ${id}`);
      return true;
    } catch (error) {
      console.error('❌ Error marking sync item processed:', error);
      return false;
    }
  }

  /**
   * Markiert eine Sync-Operation als fehlgeschlagen
   */
  async markSyncItemFailed(id, error) {
    try {
      const item = await this.get('sync_queue', id);

      if (item) {
        await this.put('sync_queue', {
          ...item,
          status: item.retry_count >= 3 ? 'failed' : 'pending',
          retry_count: (item.retry_count || 0) + 1,
          last_error: error,
          last_attempt: new Date().toISOString()
        });
      }

      return true;
    } catch (err) {
      console.error('❌ Error marking sync item failed:', err);
      return false;
    }
  }

  /**
   * Leert die gesamte Sync-Queue
   */
  async clearSyncQueue() {
    return this.clearStore('sync_queue');
  }

  // ============================================================================
  // META DATA
  // ============================================================================

  /**
   * Speichert Meta-Daten (z.B. letzter Sync-Zeitpunkt)
   */
  async setMeta(key, value) {
    try {
      await this.put('meta', { key, value, updated_at: new Date().toISOString() });
      return true;
    } catch (error) {
      console.error('❌ Error setting meta:', error);
      return false;
    }
  }

  /**
   * Holt Meta-Daten
   */
  async getMeta(key) {
    try {
      const item = await this.get('meta', key);
      return item?.value || null;
    } catch (error) {
      console.error('❌ Error getting meta:', error);
      return null;
    }
  }

  /**
   * Speichert den letzten Sync-Zeitpunkt
   */
  async setLastSyncTime() {
    return this.setMeta('lastSyncTime', new Date().toISOString());
  }

  /**
   * Holt den letzten Sync-Zeitpunkt
   */
  async getLastSyncTime() {
    return this.getMeta('lastSyncTime');
  }

  // ============================================================================
  // UTILITY
  // ============================================================================

  /**
   * Gibt Speicherstatistiken zurück
   */
  async getStorageStats() {
    try {
      const stats = {
        shopping_lists: (await this.getAll('shopping_lists')).length,
        calendar_appointments: (await this.getAll('calendar_appointments')).length,
        sync_queue: (await this.getAll('sync_queue')).length,
        lastSyncTime: await this.getLastSyncTime()
      };

      // Storage-Schätzung (falls verfügbar)
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        stats.usage = estimate.usage;
        stats.quota = estimate.quota;
        stats.usagePercent = Math.round((estimate.usage / estimate.quota) * 100);
      }

      return stats;
    } catch (error) {
      console.error('❌ Error getting storage stats:', error);
      return {};
    }
  }

  /**
   * Löscht alle Offline-Daten
   */
  async clearAllData() {
    try {
      await this.clearStore('shopping_lists');
      await this.clearStore('calendar_appointments');
      await this.clearStore('sync_queue');
      await this.clearStore('meta');
      console.log('🗑️ All IndexedDB data cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing all data:', error);
      return false;
    }
  }
}

// Singleton-Instanz exportieren
export const indexedDBService = new IndexedDBService();

console.log('✅ indexedDB.service.js v1.0.0 loaded');
